import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import LoadingSpinner from './LoadingSpinner.jsx';
import { campaignNoticeKey, useDocumentNoticeDismissal } from './DismissibleNotice.jsx';

const STAGES = Object.freeze([
  Object.freeze({ key: 'preparing', label: 'Preparing approved campaign' }),
  Object.freeze({ key: 'campaign', label: 'Creating or reconciling campaign' }),
  Object.freeze({ key: 'ad_set', label: 'Creating or reconciling shared ad set' }),
  Object.freeze({ key: 'ads', label: 'Creating approved ads' }),
  Object.freeze({ key: 'verifying_paused', label: 'Verifying PAUSED state' }),
  Object.freeze({ key: 'finishing', label: 'Finishing durable creation' }),
]);
const STAGE_INDEX = new Map(STAGES.map((stage, index) => [stage.key, index]));
STAGE_INDEX.set('complete', STAGES.length);
const PROGRESS_STATES = new Set(['not_started', 'running', 'failed', 'succeeded']);
const CAMPAIGN_STATUSES = new Set([
  'draft',
  'awaiting_approval',
  'approved',
  'publishing',
  'paused',
  'active',
  'partial',
  'failed',
  'completed',
  'archived',
]);

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function integer(value, fallback = 0) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : fallback;
}

function optionalTotal(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 1 && number <= 50 ? number : null;
}

export function normalizeCuratedMetaPublishProgress(value, fallbackTotalAds = null) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const requestedState = PROGRESS_STATES.has(source.state)
    ? source.state
    : 'not_started';
  const requestedStage = STAGE_INDEX.has(source.stage) ? source.stage : 'preparing';
  const totalAds = optionalTotal(source.totalAds) ?? optionalTotal(fallbackTotalAds);
  const rawCompletedAds = integer(source.completedAds);
  const rawPausedAds = integer(source.pausedAds);
  const rawCampaignCount = integer(source.campaignCount);
  const rawAdSetCount = integer(source.adSetCount);
  const completedAds = Math.min(rawCompletedAds, totalAds ?? 50);
  const pausedAds = Math.min(rawPausedAds, completedAds);
  const campaignCount = Math.min(rawCampaignCount, 1);
  const adSetCount = Math.min(rawAdSetCount, 1);
  const allPaused = source.allPaused === true
    && rawCampaignCount === 1
    && rawAdSetCount === 1
    && totalAds !== null
    && rawCompletedAds === totalAds
    && rawPausedAds === totalAds;
  const exactSuccess = requestedState !== 'succeeded'
    || (requestedStage === 'complete' && allPaused);
  const state = exactSuccess ? requestedState : 'running';
  const stage = exactSuccess
    ? requestedStage
    : 'verifying_paused';
  const reasonTitle = state === 'failed' ? safeReasonTitle(source.reasonTitle) : null;
  const retryNotBeforeAt = state === 'failed' && source.retryable === true
    ? safeRetryNotBeforeAt(source.retryNotBeforeAt)
    : null;
  // OGC-453: the walk stopped because BeyondLeads was interrupted, not
  // because Meta refused; only a failed projection can say so.
  const interrupted = state === 'failed' && source.interrupted === true;
  return Object.freeze({
    state,
    stage,
    campaignCount,
    adSetCount,
    completedAds,
    pausedAds,
    totalAds,
    allPaused,
    retryable: source.retryable === true,
    // Present only when Meta supplied a title for a failed projection; the
    // normalized shape is otherwise unchanged for every consumer (OGC-436).
    ...(reasonTitle ? { reasonTitle } : {}),
    // OGC-448: when Meta throttled the ad account, the instant it said access
    // returns; absent otherwise.
    ...(retryNotBeforeAt ? { retryNotBeforeAt } : {}),
    ...(interrupted ? { interrupted: true } : {}),
  });
}

const MAX_RETRY_NOT_BEFORE_MS = 24 * 60 * 60 * 1_000;

function safeRetryNotBeforeAt(value, nowMs = Date.now()) {
  if (typeof value !== 'string') return null;
  const at = Date.parse(value);
  // Meta never reports more than a day; anything further out is not a wait.
  if (!Number.isFinite(at) || at - nowMs > MAX_RETRY_NOT_BEFORE_MS) return null;
  return new Date(at).toISOString();
}

// OGC-448: the wait, re-evaluated when the instant passes so a held control
// releases without a refresh. Progress polling stops on a failed projection,
// so the timer is the only thing that moves the clock forward.
export function useCuratedMetaPublishRetryWait(progress) {
  const [, setTick] = useState(0);
  const untilMs = Date.parse(progress?.retryNotBeforeAt || '');
  useEffect(() => {
    if (!Number.isFinite(untilMs)) return undefined;
    const remaining = untilMs - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setTick((n) => n + 1), Math.min(remaining + 500, MAX_RETRY_NOT_BEFORE_MS));
    return () => clearTimeout(timer);
  }, [untilMs]);
  return curatedMetaPublishRetryWait(progress);
}

// OGC-448: how long a retry has to wait, from the projection and the reader's
// clock. `blocked` while the instant is in the future; `minutes` is rounded
// up and never below one so the copy never says "0 min".
export function curatedMetaPublishRetryWait(progress, nowMs = Date.now()) {
  const at = Date.parse(progress?.retryNotBeforeAt || '');
  if (!Number.isFinite(at) || at <= nowMs) return Object.freeze({ blocked: false, minutes: 0, untilMs: null });
  return Object.freeze({
    blocked: true,
    minutes: Math.max(1, Math.ceil((at - nowMs) / 60_000)),
    untilMs: at,
  });
}

export function curatedMetaPublishRetryWaitText(wait) {
  if (!wait?.blocked) return '';
  const until = new Date(wait.untilMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `Meta is rate limiting this ad account. Retry after about ${wait.minutes} min (around ${until}); retrying sooner will fail the same way.`;
}

// Meta's end-user title for the refusal (OGC-436): short, already bounded by
// the server, re-bounded here; anything else renders as no reason.
const MAX_REASON_TITLE_LENGTH = 120;
export function safeReasonTitle(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_REASON_TITLE_LENGTH) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(trimmed) || /[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return null;
  return trimmed;
}

// A draft has no workflow/approval binding until the create request seals one,
// so the server reports `binding: null` with `progress.state: 'not_started'`
// for the entire pre-seal window (draft, awaiting_approval, approved). That
// shape is only trustworthy when the caller has not pinned a specific
// workflow/approval pair to watch — once a binding is expected, every rule
// below still applies exactly as before.
export function isCuratedMetaPublishProgressResponse(value, campaignId, expectedBinding = {}) {
  const response = plainObject(value);
  const campaign = plainObject(response?.campaign);
  const binding = plainObject(response?.binding);
  const progress = plainObject(response?.progress);
  const expectedCampaignId = String(campaignId || '').trim();
  const expectedWorkflowJobId = String(expectedBinding.workflowJobId || '').trim();
  const expectedApprovalRequestId = String(expectedBinding.approvalRequestId || '').trim();
  const noBindingExpected = !expectedWorkflowJobId && !expectedApprovalRequestId;
  const preSealBindingNull = Boolean(response)
    && response.binding === null
    && noBindingExpected
    && Boolean(progress)
    && progress.state === 'not_started';
  if (
    !response
    || !campaign
    || !progress
    || !expectedCampaignId
    || campaign.id !== expectedCampaignId
    || !CAMPAIGN_STATUSES.has(campaign.status)
    || !PROGRESS_STATES.has(progress.state)
    || !STAGE_INDEX.has(progress.stage)
    || typeof progress.allPaused !== 'boolean'
    || typeof progress.retryable !== 'boolean'
    || (progress.reasonTitle !== undefined && progress.reasonTitle !== null && typeof progress.reasonTitle !== 'string')
    || (progress.interrupted !== undefined && typeof progress.interrupted !== 'boolean')
    || (!preSealBindingNull && (
      !binding
      || typeof binding.workflowJobId !== 'string'
      || !binding.workflowJobId.trim()
      || typeof binding.approvalRequestId !== 'string'
      || !binding.approvalRequestId.trim()
      || (expectedWorkflowJobId && binding.workflowJobId !== expectedWorkflowJobId)
      || (expectedApprovalRequestId && binding.approvalRequestId !== expectedApprovalRequestId)
    ))
  ) return false;
  const totalAds = progress.totalAds;
  const campaignCount = progress.campaignCount;
  const adSetCount = progress.adSetCount;
  const completedAds = progress.completedAds;
  const pausedAds = progress.pausedAds;
  if (
    (totalAds !== null
      && (!Number.isSafeInteger(totalAds) || totalAds < 1 || totalAds > 50))
    || !Number.isSafeInteger(campaignCount)
    || campaignCount < 0
    || campaignCount > 1
    || !Number.isSafeInteger(adSetCount)
    || adSetCount < 0
    || adSetCount > 1
    || !Number.isSafeInteger(completedAds)
    || completedAds < 0
    || completedAds > 50
    || !Number.isSafeInteger(pausedAds)
    || pausedAds < 0
    || pausedAds > completedAds
    || (totalAds !== null && completedAds > totalAds)
  ) return false;
  const exactPausedHierarchy = campaignCount === 1
    && adSetCount === 1
    && totalAds !== null
    && completedAds === totalAds
    && pausedAds === totalAds;
  if (progress.allPaused === true && !exactPausedHierarchy) return false;
  if (progress.state === 'not_started') {
    const allowedStatuses = preSealBindingNull
      ? new Set(['draft', 'awaiting_approval', 'approved'])
      : new Set(['approved']);
    return allowedStatuses.has(campaign.status)
      && progress.stage === 'preparing'
      && campaignCount === 0
      && adSetCount === 0
      && completedAds === 0
      && pausedAds === 0
      && totalAds === null
      && progress.allPaused === false
      && progress.retryable === false;
  }
  if (progress.state === 'succeeded') {
    return campaign.status === 'paused'
      && progress.stage === 'complete'
      && exactPausedHierarchy
      && progress.allPaused === true
      && progress.retryable === false;
  }
  return progress.stage !== 'complete';
}

export function initialCuratedMetaPublishProgress(totalAds) {
  return normalizeCuratedMetaPublishProgress({
    state: 'running',
    stage: 'preparing',
    totalAds,
  });
}

export function mergeCuratedMetaPublishProgress(current, incoming, fallbackTotalAds = null) {
  const previous = normalizeCuratedMetaPublishProgress(current, fallbackTotalAds);
  const next = normalizeCuratedMetaPublishProgress(
    incoming,
    previous.totalAds ?? fallbackTotalAds,
  );
  if (previous.state === 'succeeded' && next.state !== 'succeeded') return previous;
  if (previous.state === 'running' && next.state === 'not_started') return current;
  const nextStage = STAGE_INDEX.get(next.stage) ?? 0;
  const previousStage = STAGE_INDEX.get(previous.stage) ?? 0;
  const merged = normalizeCuratedMetaPublishProgress({
    ...next,
    stage: nextStage >= previousStage ? next.stage : previous.stage,
    campaignCount: Math.max(previous.campaignCount, next.campaignCount),
    adSetCount: Math.max(previous.adSetCount, next.adSetCount),
    completedAds: Math.max(previous.completedAds, next.completedAds),
    pausedAds: Math.max(previous.pausedAds, next.pausedAds),
    totalAds: next.totalAds ?? previous.totalAds,
    allPaused: previous.allPaused || next.allPaused,
  });
  const previousIdentity = JSON.stringify(previous);
  if (previousIdentity !== JSON.stringify(merged)) return merged;
  return current && typeof current === 'object' && JSON.stringify(current) === previousIdentity
    ? current
    : previous;
}

export function shouldPollCuratedMetaPublishProgress(progress) {
  return normalizeCuratedMetaPublishProgress(progress).state === 'running';
}

// OGC-438: `continueOnNotStarted` exists for the window between a seal and
// the executor's claim (measured 10-30 s on staging, see OGC-409). An intent
// that was sealed but never claimed projects `not_started` for good, so the
// continuation is bounded: after this many consecutive successful not-started
// reads the poll stops and the static approved view stands. A successful
// read of any other state resets the count; a thrown read neither counts
// nor resets. Consumers pass `Infinity` while their own create/resume
// request is in flight — that window is click→claim (two Meta verifications
// plus the seal since OGC-432) and the request's return restarts the loop.
export const CURATED_META_PUBLISH_NOT_STARTED_READ_LIMIT = 40;

export function shouldScheduleCuratedMetaPublishProgressRead(
  progress,
  {
    continueOnNotStarted = false,
    continueOnFailed = false,
    notStartedReads = 0,
    notStartedReadLimit = CURATED_META_PUBLISH_NOT_STARTED_READ_LIMIT,
  } = {},
) {
  const state = normalizeCuratedMetaPublishProgress(progress).state;
  return state === 'running'
    || (state === 'not_started' && continueOnNotStarted && notStartedReads < notStartedReadLimit)
    || (state === 'failed' && continueOnFailed);
}

// The poll loop's bookkeeping, factored out so the bound is testable without
// rendering the hook: counts consecutive not-started reads and says whether
// the next read should be scheduled.
export function nextCuratedMetaPublishProgressPoll(observedProgress, notStartedReads, options = {}, { readSucceeded = true } = {}) {
  const state = normalizeCuratedMetaPublishProgress(observedProgress).state;
  // A thrown read leaves the last observation in place and does not touch
  // the count: an outage must keep retrying exactly as it did before the bound.
  const reads = !readSucceeded ? notStartedReads : state === 'not_started' ? notStartedReads + 1 : 0;
  return {
    notStartedReads: reads,
    schedule: shouldScheduleCuratedMetaPublishProgressRead(observedProgress, { ...options, notStartedReads: reads }),
  };
}

export async function loadCuratedMetaPublishProgress({
  requestApi = api,
  companyId,
  campaignId,
  workflowJobId,
  approvalRequestId,
  signal,
} = {}) {
  const expectedCampaignId = String(campaignId || '').trim();
  const response = await requestApi.get(
    `/api/saas/campaigns/${encodeURIComponent(expectedCampaignId)}/meta-publish-progress`,
    {
      headers: { 'x-company-id': String(companyId || '').trim() },
      ...(signal ? { signal } : {}),
    },
  );
  if (!isCuratedMetaPublishProgressResponse(response, expectedCampaignId, {
    workflowJobId,
    approvalRequestId,
  })) {
    throw new Error('Meta creation status is unavailable.');
  }
  return {
    progress: normalizeCuratedMetaPublishProgress(response?.progress),
    campaignStatus: String(response?.campaign?.status || ''),
  };
}

// OGC-409: an observation recorded while polling was enabled must not
// outlive that poll. `boundScope` (company:campaign:workflowJobId:
// approvalRequestId) does not change on activation or archive, so the
// scope-change effect never resets the stored snapshot when the campaign
// leaves META_CREATION_PROGRESS_STATES with no live publish intent — the
// last-read `observedCampaignStatus` would otherwise persist indefinitely
// with no poll left able to refresh or correct it, and a consumer diffing it
// against the current durable status (see
// curatedCampaignObservedStatusReconciliationKey in CuratedCampaigns.jsx)
// would fire reconciliation retries against a comparison that can never
// resolve. Gate the exposed value on `enabled` instead of on the retained
// snapshot so a stale observation can never drive any consumer once the poll
// that produced it can no longer correct it. Exported so the gate itself is
// directly unit-testable without rendering the hook.
export function curatedMetaPublishProgressVisibleObservedStatus(enabled, observedCampaignStatus) {
  return enabled ? observedCampaignStatus : '';
}

export function yieldCuratedMetaPublishProgressPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      // Two frames ensure React's synchronous preparing state has crossed an
      // actual paint boundary before the long execute request is dispatched.
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      return;
    }
    setTimeout(resolve, 0);
  });
}

export function useCuratedMetaPublishProgress({
  campaignId,
  companyId,
  workflowJobId,
  approvalRequestId,
  enabled = false,
  continueOnNotStarted = false,
  continueOnFailed = false,
  notStartedReadLimit = CURATED_META_PUBLISH_NOT_STARTED_READ_LIMIT,
  totalAds = null,
  pollIntervalMs = 1_500,
  requestApi = api,
} = {}) {
  const trimmedCompanyId = String(companyId || '').trim();
  const trimmedCampaignId = String(campaignId || '').trim();
  const trimmedWorkflowJobId = String(workflowJobId || '').trim();
  const trimmedApprovalRequestId = String(approvalRequestId || '').trim();
  const isBound = Boolean(
    trimmedCompanyId && trimmedCampaignId && trimmedWorkflowJobId && trimmedApprovalRequestId,
  );
  const boundScope = isBound
    ? [trimmedCompanyId, trimmedCampaignId, trimmedWorkflowJobId, trimmedApprovalRequestId].join(':')
    : '';
  // A draft has no workflow/approval binding until the create request seals
  // one. While a request is in flight (or the campaign is already
  // publishing), poll a provisional company:campaign scope instead of
  // waiting on a separate binding-discovery step — the server's
  // binding-null, not_started response is what makes that scope pollable at
  // all (see isCuratedMetaPublishProgressResponse). `begin()` may seed this
  // same provisional scope from the click itself, before continueOnNotStarted
  // has re-rendered true, so it is computed independent of that flag.
  const provisionalScope = !isBound && trimmedCompanyId && trimmedCampaignId
    ? `${trimmedCompanyId}:${trimmedCampaignId}`
    : '';
  const scope = boundScope || (continueOnNotStarted ? provisionalScope : '');
  const beginScope = boundScope || provisionalScope;
  const [snapshot, setSnapshot] = useState(() => ({
    scope,
    progress: normalizeCuratedMetaPublishProgress(null, totalAds),
    hasRead: false,
    readUnavailable: false,
    observedCampaignStatus: '',
  }));
  const [pollGeneration, setPollGeneration] = useState(0);
  const requestVersion = useRef(0);

  useEffect(() => {
    requestVersion.current += 1;
    setSnapshot((current) => {
      // The provisional scope and its eventual bound scope (once the seal
      // commits) name the same company:campaign pair. Preserve a running
      // staged snapshot across that one transition so the "Preparing" view
      // does not blink back to not_started while the real read catches up;
      // any other scope change (a different campaign, or leaving the
      // provisional/bound scope entirely) still resets normally.
      const scopeChanged = current.scope !== scope;
      const previousBase = current.scope.split(':').slice(0, 2).join(':');
      const nextBase = scope.split(':').slice(0, 2).join(':');
      const sameCampaignTransition = scopeChanged
        && Boolean(previousBase)
        && previousBase === nextBase
        && current.progress?.state === 'running';
      if (sameCampaignTransition) {
        return {
          scope,
          progress: current.progress,
          hasRead: false,
          readUnavailable: false,
          observedCampaignStatus: '',
        };
      }
      return {
        scope,
        progress: normalizeCuratedMetaPublishProgress(null, totalAds),
        hasRead: false,
        readUnavailable: false,
        observedCampaignStatus: '',
      };
    });
  }, [scope, totalAds]);

  const begin = useCallback((expectedAds = totalAds) => {
    if (!beginScope) return;
    requestVersion.current += 1;
    setSnapshot({
      scope: beginScope,
      progress: initialCuratedMetaPublishProgress(expectedAds),
      hasRead: false,
      readUnavailable: false,
      observedCampaignStatus: '',
    });
  }, [beginScope, totalAds]);

  const reconnect = useCallback(() => {
    if (!scope) return;
    requestVersion.current += 1;
    setSnapshot((current) => current.scope === scope
      ? { ...current, hasRead: false, readUnavailable: false }
      : current);
    setPollGeneration((generation) => generation + 1);
  }, [scope]);

  useEffect(() => {
    if (!enabled || !scope) return undefined;
    const version = ++requestVersion.current;
    let active = true;
    let timer = null;
    let controller = null;
    let observedProgress = normalizeCuratedMetaPublishProgress(null, totalAds);
    let notStartedReads = 0;
    const delay = Number.isFinite(Number(pollIntervalMs))
      ? Math.max(250, Math.min(Number(pollIntervalMs), 30_000))
      : 1_500;

    const poll = async () => {
      if (!active || requestVersion.current !== version) return;
      controller = new AbortController();
      let result = null;
      let readSucceeded = false;
      try {
        result = await loadCuratedMetaPublishProgress({
          requestApi,
          companyId,
          campaignId,
          workflowJobId,
          approvalRequestId,
          signal: controller.signal,
        });
        if (!active || requestVersion.current !== version) return;
        observedProgress = result.progress;
        readSucceeded = true;
        setSnapshot((current) => {
          if (current.scope !== scope) return current;
          return {
            scope,
            progress: mergeCuratedMetaPublishProgress(current.progress, result.progress, totalAds),
            hasRead: true,
            readUnavailable: false,
            observedCampaignStatus: result.campaignStatus,
          };
        });
      } catch (error) {
        if (!active || requestVersion.current !== version || error?.name === 'AbortError') return;
        setSnapshot((current) => current.scope === scope
          ? { ...current, readUnavailable: true }
          : current);
      }
      if (!active || requestVersion.current !== version) return;
      const next = nextCuratedMetaPublishProgressPoll(observedProgress, notStartedReads, { continueOnNotStarted, continueOnFailed, notStartedReadLimit }, { readSucceeded });
      notStartedReads = next.notStartedReads;
      if (next.schedule) timer = setTimeout(poll, delay);
    };

    poll();
    return () => {
      active = false;
      requestVersion.current += 1;
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [
    campaignId,
    companyId,
    workflowJobId,
    approvalRequestId,
    continueOnFailed,
    continueOnNotStarted,
    enabled,
    notStartedReadLimit,
    pollGeneration,
    pollIntervalMs,
    requestApi,
    scope,
    totalAds,
  ]);

  const current = snapshot.scope === scope
    ? snapshot
    : {
      scope,
      progress: normalizeCuratedMetaPublishProgress(null, totalAds),
      hasRead: false,
      readUnavailable: false,
      observedCampaignStatus: '',
    };
  return Object.freeze({
    progress: current.progress,
    hasRead: current.hasRead,
    readUnavailable: current.readUnavailable,
    // See curatedMetaPublishProgressVisibleObservedStatus above.
    observedCampaignStatus: curatedMetaPublishProgressVisibleObservedStatus(
      enabled,
      current.observedCampaignStatus,
    ),
    begin,
    reconnect,
  });
}

function stageLabel(stage) {
  if (stage === 'complete') return 'Creation complete';
  return STAGES.find((candidate) => candidate.key === stage)?.label || STAGES[0].label;
}

function failedStageLabel(stage) {
  return ({
    preparing: 'preparation',
    campaign: 'campaign',
    ad_set: 'shared ad set',
    ads: 'approved ads',
    verifying_paused: 'PAUSED verification',
    finishing: 'final reconciliation',
    complete: 'final reconciliation',
  })[stage] || 'preparation';
}

function stageAnnouncement(progress) {
  const label = stageLabel(progress.stage);
  if (progress.stage === 'ads' || progress.stage === 'verifying_paused') {
    return `${label}; ${progress.pausedAds} of ${progress.totalAds ?? 'the expected'} approved ads confirmed PAUSED`;
  }
  return label;
}

function terminalCounts(progress) {
  const quantity = (value, singular, plural = `${singular}s`) => (
    `${value} ${value === 1 ? singular : plural}`
  );
  return [
    quantity(progress.campaignCount, 'campaign'),
    quantity(progress.adSetCount, 'shared ad set'),
    quantity(progress.completedAds, 'approved ad'),
  ].join(' · ');
}

export function CuratedMetaPublishProgress({
  progress,
  companyId = '',
  campaignId = '',
  titleId = 'bl-meta-publish-progress-title',
  dismissible = true,
}) {
  const current = normalizeCuratedMetaPublishProgress(progress);
  const { dismissed, dismiss, canDismiss } = useDocumentNoticeDismissal(
    dismissible && current.state === 'succeeded'
      ? campaignNoticeKey({ companyId, campaignId, kind: 'meta-creation-succeeded' })
      : '',
  );
  // Hooks before the early return: the panel renders nothing for not_started
  // and must keep the same hook order once the projection starts running.
  const retryWait = useCuratedMetaPublishRetryWait(current);
  if (current.state === 'not_started' || (dismissible && current.state === 'succeeded' && dismissed)) return null;
  const activeIndex = STAGE_INDEX.get(current.stage) ?? 0;
  const running = current.state === 'running';
  const failed = current.state === 'failed';
  const announcement = running ? stageAnnouncement(current) : '';
  const safeFailedStage = failedStageLabel(current.stage);
  const interrupted = failed && current.interrupted === true;
  const liveAnnouncement = failed
    ? `${interrupted ? `Creation was interrupted at ${safeFailedStage}` : `Creation paused safely at ${safeFailedStage}`}.${current.reasonTitle ? ` Meta reported: ${current.reasonTitle}.` : ''} ${retryWait.blocked
      ? curatedMetaPublishRetryWaitText(retryWait)
      : current.retryable
        ? 'Retry resumes this same attempt.'
        : 'Refresh the durable status before continuing.'}`
    : current.state === 'succeeded'
      ? `Created in Meta. ${terminalCounts(current)}. Every object is PAUSED.`
      : announcement;

  return (
    <section
      className={`bl-meta-publish-progress is-${current.state}`}
      aria-labelledby={titleId}
    >
      <div className="bl-meta-publish-progress-heading">
        <div>
          <div className="bl-kicker">PAUSED — no spend</div>
          <h2 id={titleId}>{current.state === 'succeeded'
            ? 'Created in Meta — all objects PAUSED'
            : failed
              ? (interrupted ? `Creation was interrupted at ${safeFailedStage}` : `Creation paused safely at ${safeFailedStage}`)
              : 'Meta creation in progress'}</h2>
        </div>
        {running && <LoadingSpinner size={22} />}
        {dismissible && current.state === 'succeeded' && canDismiss && (
          <button type="button" className="bl-dismissible-notice-close"
            aria-label="Dismiss Meta creation notice" onClick={dismiss}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {running && (
        <>
          <div
            className="bl-meta-publish-progress-meter"
            role="progressbar"
            aria-valuemin="1"
            aria-valuemax={STAGES.length}
            aria-valuenow={Math.min(activeIndex + 1, STAGES.length)}
            aria-valuetext={announcement}
          >
            <span style={{ '--bl-meta-progress-stage': Math.min(activeIndex + 1, STAGES.length) }} />
          </div>
          <p
            className="bl-meta-publish-progress-announcement"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >{liveAnnouncement}</p>
          <ol className="bl-meta-publish-stage-list" aria-hidden="true">
            {STAGES.map((stage, index) => (
              <li
                key={stage.key}
                className={index < activeIndex ? 'is-complete' : index === activeIndex ? 'is-current' : ''}
              >{stage.label}</li>
            ))}
          </ol>
          <p className="bl-meta-publish-progress-note">Video upload and Meta verification can take a few minutes. You can refresh or leave this page; BeyondLeads will reconnect to this same creation attempt.</p>
        </>
      )}

      {current.state === 'succeeded' && (
        <>
          <p className="bl-meta-publish-progress-counts" role="status" aria-live="polite" aria-atomic="true">{terminalCounts(current)}</p>
          <p className="bl-meta-publish-progress-note">Every confirmed object is PAUSED — no spend until an owner or admin activates the campaign separately.</p>
        </>
      )}

      {failed && (
        <>
          <p className="bl-meta-publish-progress-announcement" role="alert" aria-live="assertive" aria-atomic="true">{liveAnnouncement}</p>
          {current.reasonTitle && (
            <p className="bl-meta-publish-progress-reason">Meta reported: <strong>{current.reasonTitle}</strong></p>
          )}
          {interrupted && (
            <p className="bl-meta-publish-progress-reason" data-interrupted="true">{current.reasonTitle
              ? 'BeyondLeads stopped before it finished, after Meta refused an earlier object. Every confirmed object is still PAUSED.'
              : 'BeyondLeads stopped before it finished; Meta refused nothing. Every confirmed object is still PAUSED.'}</p>
          )}
          <p className="bl-meta-publish-progress-counts">{terminalCounts(current)}</p>
          {retryWait.blocked && (
            <p className="bl-meta-publish-progress-reason" data-retry-wait="true">{curatedMetaPublishRetryWaitText(retryWait)}</p>
          )}
          <p className="bl-meta-publish-progress-note">{current.retryable
            ? 'Retry resumes this same attempt from its confirmed checkpoints and creates only missing objects.'
            : 'No additional Meta objects will be created until this stage is corrected. Refresh the durable status before continuing.'}</p>
        </>
      )}
    </section>
  );
}

export const curatedMetaPublishProgressInternalsForTest = Object.freeze({
  STAGES,
  stageAnnouncement,
  stageLabel,
});
